import React from 'react';
import { connect } from 'react-redux';

import { Button, Flex, FlexItem, Text, Grid, GridItem, Form, Modal, ButtonVariant, TextArea } from '@patternfly/react-core';
import DatePicker from 'react-datepicker';

import { EmployeeAvailability, Availabilityreason } from 'domain/EmployeeAvailability';
import { Employee } from 'domain/Employee';
import { AppState } from 'store/types';
import { employeeSelectors } from 'store/employee';

import 'react-datepicker/dist/react-datepicker.css';
import TypeaheadSelectInput from 'ui/components/TypeaheadSelectInput';
import { withTranslation, WithTranslation } from 'react-i18next';

interface Props {
  tenantId: number;
  availability?: EmployeeAvailability;
  isOpen: boolean;
  employeeList: Employee[];
  // eslint-disable-next-line no-unused-vars
  onSave: (availability: EmployeeAvailability) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete: (availability: EmployeeAvailability) => void;
  onClose: () => void;
}

const mapStateToProps = (state: AppState, ownProps: {
  availability?: EmployeeAvailability;
  isOpen: boolean;
  // eslint-disable-next-line no-unused-vars
  onSave: (availability: EmployeeAvailability) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete: (availability: EmployeeAvailability) => void;
  onClose: () => void;
}): Props => ({
  ...ownProps,
  tenantId: state.tenantData.currentTenantId,
  employeeList: employeeSelectors.getActiveEmployeeList(state),
});

interface State {
  resetCount: number;
  editedValue: Partial<EmployeeAvailability>;
}

export class EditAvailabilityModal extends React.Component<Props & WithTranslation, State> {
  constructor(props: Props & WithTranslation) {
    super(props);

    this.onSave = this.onSave.bind(this);
    this.state = {
      resetCount: 0,
      editedValue: { ...this.props.availability },
    };
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.availability === undefined && prevProps.availability !== undefined) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ resetCount: prevState.resetCount + 1,
        editedValue: {
          tenantId: this.props.tenantId,
        } });
    } else if (this.props.availability !== undefined
      && (this.props.availability.id !== prevState.editedValue.id
        || this.props.availability.version !== prevState.editedValue.version)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ resetCount: prevState.resetCount + 1, editedValue: this.props.availability });
    }
  }

  onSave() {
    const availability = this.state.editedValue;
    if (availability.employee !== undefined && availability.startDateTime !== undefined
      && availability.endDateTime !== undefined && availability.state !== undefined) {
      this.props.onSave({ ...availability, tenantId: this.props.tenantId } as EmployeeAvailability);
    }
  }

  render() {
    const dateFormat = 'MMMM dd, hh:mm a';
    const { t, tReady } = this.props;
    if (!tReady) {
      return null;
    }
    return (
      <Modal
        title={this.props.availability ? t('editAvailability') : t('createAvailability')}
        onClose={this.props.onClose}
        isOpen={this.props.isOpen}
        actions={
          [
            <Button
              aria-label="Close Modal"
              variant={ButtonVariant.tertiary}
              key={0}
              onClick={this.props.onClose}
            >
              {t('close')}
            </Button>,
          ].concat(this.props.availability ? [
            <Button
              aria-label="Delete"
              variant={ButtonVariant.danger}
              key={1}
              onClick={() => this.props.onDelete(this.props.availability as EmployeeAvailability)}
            >
              {t('delete')}
            </Button>,
          ] : []).concat([
            <Button aria-label="Save" key={2} onClick={this.onSave}>{t('save')}</Button>,
          ])
        }
        variant="small"
        width={650}
      >
        <Form id="modal-element" key={this.state.resetCount} onSubmit={(e) => e.preventDefault()}>
          <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
            <FlexItem>
              <Grid>
                <GridItem span={4}>
                  <Text>{t('availabilityStart')}</Text>
                </GridItem>
                <GridItem span={8}>
                  <DatePicker
                    aria-label="Availability Start"
                    selected={this.state.editedValue.startDateTime}
                    onChange={(date) => this.setState((prevState) => ({
                      editedValue: { ...prevState.editedValue, startDateTime: (date !== null) ? date : undefined },
                    }))}
                    dateFormat={dateFormat}
                    showTimeSelect
                  />
                </GridItem>
              </Grid>
            </FlexItem>
            <FlexItem>
              <Grid>
                <GridItem span={4}>
                  <Text>{t('availabilityEnd')}</Text>
                </GridItem>
                <GridItem span={8}>
                  <DatePicker
                    aria-label="Availability End"
                    selected={this.state.editedValue.endDateTime}
                    onChange={(date) => this.setState((prevState) => ({
                      editedValue: { ...prevState.editedValue, endDateTime: (date !== null) ? date : undefined },
                    }))}
                    dateFormat={dateFormat}
                    showTimeSelect
                  />
                </GridItem>
              </Grid>
            </FlexItem>
            <FlexItem>
              <Grid>
                <GridItem span={4}>
                  <Text>{t('employee')}</Text>
                </GridItem>
                <GridItem span={8}>
                  <TypeaheadSelectInput
                    aria-label="Employee"
                    emptyText={t('selectEmployee')}
                    value={this.state.editedValue.employee}
                    options={this.props.employeeList}
                    optionToStringMap={(employee) => employee.name}
                    onChange={(employee) => this.setState((prevState) => ({
                      editedValue: { ...prevState.editedValue, employee },
                    }))}
                  />
                </GridItem>
              </Grid>
            </FlexItem>
            <FlexItem>
              <Grid>
                <GridItem span={4}>
                  <Text>{t('type')}</Text>
                </GridItem>
                <GridItem span={8}>
                  <TypeaheadSelectInput
                    aria-label="Type"
                    emptyText={t('selectType')}
                    value={this.state.editedValue.state}
                    options={['UNAVAILABLE', 'DESIRED', 'UNDESIRED'] as ('UNAVAILABLE'|'DESIRED'|'UNDESIRED')[]}
                    optionToStringMap={(state) => this.props.t(`EmployeeAvailabilityState.${state}`)}
                    onChange={(state) => this.setState((prevState) => ({
                      editedValue: { ...prevState.editedValue, state },
                    }))}
                  />
                </GridItem>
              </Grid>
            </FlexItem>
            {this.state.editedValue.state === 'UNAVAILABLE' && (
              <FlexItem>
                <Grid>
                  <GridItem span={4}>
                    <Text>{t('reason')}</Text>
                  </GridItem>

                  <GridItem span={8}>
                    <TypeaheadSelectInput
                      aria-label="Reason"
                      emptyText={t('selectReason')}
                      value={this.state.editedValue.reason}
                      options={[
                        'VACATION',
                        'SICK_LEAVE',
                        'MEDICAL_APPOINTMENT',
                        'COMPENSATORY_DAY',
                        'TRAINING',
                        'BUSINESS_TRIP',
                        'PERSONAL_LEAVE',
                        'UNPAID_LEAVE',
                        'DOMESTIC_EMERGENCY',
                        'MEDICAL_RESTRICTION',
                        'SUSPENSION',
                        'UNJUSTIFIED_ABSENCE',
                        'OTHER',
                      ] as Availabilityreason[]}
                      optionToStringMap={(reason) =>
                        this.props.t(`AvailabilityReason.${reason}`)
                      }
                      onChange={(reason) =>
                        this.setState((prevState) => ({
                          editedValue: {
                            ...prevState.editedValue,
                            reason,
                          },
                        }))
                      }
                    />
                  </GridItem>
                </Grid>
              </FlexItem>
            )}
            {this.state.editedValue.state === 'UNAVAILABLE' && (
              <FlexItem>
                <Grid>
                  <GridItem span={4}>
                    <Text>{t('observation')}</Text>
                  </GridItem>

                  <GridItem span={8}>
                    <TextArea
                      value={this.state.editedValue.observation || ''}
                      resizeOrientation="vertical"
                      onChange={(value) =>
                        this.setState((prevState) => ({
                          editedValue: {
                            ...prevState.editedValue,
                            observation: value,
                          },
                        }))
                      }
                    />
                  </GridItem>
                </Grid>
              </FlexItem>
            )}
          </Flex>
        </Form>
      </Modal>
    );
  }
}

// eslint-disable-next-line no-undef
export default withTranslation('EditAvailabilityModal')(
  connect(mapStateToProps)(EditAvailabilityModal),
);
